/**
 * EN-1851: selecting a tracksheet marker row filters the run passages
 * section to that marker. Pure helpers so they can be unit tested.
 */

/** Class on the selected tracksheet row. */
export const SELECTED_MARKER_ROW_CLASS = 'tracksheet-row--selected';

/** A click on a marker row selects it; a second click deselects it. */
export function toggleMarkerSelection(
  current: number | null,
  clicked: number | null | undefined
): number | null {
  if (clicked == null) return null;
  return clicked === current ? null : clicked;
}

/** The run passages section's filter chip. */
export function markerFilterChipLabel(name: string | null | undefined): string {
  const trimmed = (name || '').trim();
  return `Marker: ${trimmed || '(unnamed)'}`;
}

/** table-row-class-fn of the tracksheet: highlights the selected marker. */
export function markerRowClass(
  rowMarkerId: number | null | undefined,
  selected: number | null
): string {
  return rowMarkerId != null && rowMarkerId === selected
    ? SELECTED_MARKER_ROW_CLASS
    : '';
}

/** A selection survives a rows refresh only while its marker is still there. */
export function keepMarkerSelection(
  selected: number | null,
  markerIds: readonly number[]
): number | null {
  return selected != null && markerIds.includes(selected) ? selected : null;
}

/**
 * EN-1851: the selected marker outlives the tracksheet. A row click shows the
 * marker on the map, which closes the drawer (or sheet) and unmounts the
 * tracksheet, so the selection is kept here, for one run at a time: another
 * run (a run switch, or the run ended and the tracksheet shows another run or
 * none) starts with nothing selected and forgets the old one.
 */
export class MarkerSelectionMemory {
  private runKey: string | null = null;
  private markerId: number | null = null;

  /** The run's selection (null: none); no run forgets everything. */
  remember(runKey: string | null, markerId: number | null): void {
    this.runKey = runKey;
    this.markerId = runKey === null ? null : markerId;
  }

  /** The selection kept for this run; any other run (or none) starts empty. */
  restore(runKey: string | null): number | null {
    if (runKey === null || runKey !== this.runKey) {
      this.remember(runKey, null);
      return null;
    }
    return this.markerId;
  }
}

/** The tracksheet's selection memory (module-level, survives unmounts). */
export const tracksheetMarkerSelection = new MarkerSelectionMemory();

/**
 * EN-1851: a tracksheet row click. Toggles the selection and keeps it in the
 * memory at once: on a phone the click closes the sheet and the tracksheet
 * unmounts in the same update, before its selectedMarkerId watcher would run.
 */
export function selectFromRowClick(
  memory: MarkerSelectionMemory,
  runKey: string | null,
  current: number | null,
  clicked: number | null | undefined
): number | null {
  const next = toggleMarkerSelection(current, clicked);
  memory.remember(runKey, next);
  return next;
}
